import { useMemo } from "react";
import PropTypes from "prop-types";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import useScrollSpy from "../../hooks/useScrollSpy";
import SidebarNavItem from "./SidebarNavItem";
import TabSection from "./TabSection";

/**
 * Settings page layout with:
 * - Desktop/Tablet: vertical sidebar navigation + full-width header banner
 * - Mobile: grouped dropdown selector (native <select> with <optgroup>)
 *
 * Replaces TabbedPageLayout for settings-style pages where all sections
 * are on a single scrollable page.
 */
const SidebarSettingsLayout = ({ groups, sections }) => {
  const sectionIds = useMemo(
    () => sections.map((s) => s.id),
    [sections]
  );

  const { activeSection, scrollToSection } = useScrollSpy(sectionIds);

  // Group sections by their group ID
  const groupedSections = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => map.set(g.id, []));
    sections.forEach((s) => {
      const group = map.get(s.group);
      if (group) group.push(s);
    });
    return map;
  }, [groups, sections]);

  return (
    <div className="bg-cream-50 min-h-full">
      {/* Mobile: grouped dropdown selector */}
      <MobileSectionDropdown
        groups={groups}
        groupedSections={groupedSections}
        activeSection={activeSection}
        onSelect={scrollToSection}
      />

      <div className="flex">
        {/* Desktop/Tablet: vertical sidebar */}
        <DesktopSidebar
          groups={groups}
          groupedSections={groupedSections}
          activeSection={activeSection}
          onSelect={scrollToSection}
        />

        {/* Main content area */}
        <div className="flex-1 min-w-0">
          <div className="divide-y divide-ink-100">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                className="scroll-mt-[120px] md:scroll-mt-[68px]"
              >
                <TabSection
                  title={section.title}
                  description={section.description}
                  padding={section.padding || "py-8"}
                  badge={section.badge}
                >
                  {section.content}
                </TabSection>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Desktop/Tablet vertical sidebar with grouped navigation.
 * Sticky under the TopNav; scrolls internally when content exceeds the viewport.
 */
const DesktopSidebar = ({
  groups,
  groupedSections,
  activeSection,
  onSelect,
}) => {
  return (
    <aside className="hidden md:block w-48 lg:w-56 shrink-0 border-r border-ink-100 bg-cream-50">
      <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide py-6 px-3">
        <nav className="space-y-6">
          {groups.map((group) => {
            const groupSections = groupedSections.get(group.id) || [];
            if (groupSections.length === 0) return null;

            return (
              <div key={group.id}>
                <h3 className="px-3 smallcaps text-ink-400">
                  {group.label}
                </h3>
                <div className="mt-1.5 space-y-0.5">
                  {groupSections.map((section) => (
                    <SidebarNavItem
                      key={section.id}
                      label={section.title}
                      active={activeSection === section.id}
                      onClick={() => onSelect(section.id)}
                      badge={section.badge}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

/**
 * Mobile: native <select> dropdown with <optgroup> for category grouping.
 * Sticky at top so it's always accessible while scrolling.
 * Follows the same pattern as the existing Tabs.js component.
 */
const MobileSectionDropdown = ({
  groups,
  groupedSections,
  activeSection,
  onSelect,
}) => {
  const handleChange = (e) => {
    onSelect(e.target.value);
  };

  return (
    <div className="md:hidden sticky top-16 z-10 bg-cream-50/95 backdrop-blur border-b border-ink-100 px-4 py-3">
      <div className="grid grid-cols-1">
        <select
          value={activeSection}
          aria-label="Navigate to section"
          className="col-start-1 row-start-1 w-full appearance-none bg-none rounded-card bg-white py-2 pl-3 pr-8 text-sm font-medium text-ink-900 border border-ink-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none"
          onChange={handleChange}
        >
          {groups.map((group) => {
            const groupSections = groupedSections.get(group.id) || [];
            if (groupSections.length === 0) return null;

            return (
              <optgroup key={group.id} label={group.label}>
                {groupSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.title}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end fill-ink-400"
        />
      </div>
    </div>
  );
};

SidebarSettingsLayout.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
      padding: PropTypes.string,
      badge: PropTypes.string,
    })
  ).isRequired,
};

export default SidebarSettingsLayout;
